import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authAPI } from '../utils/supabase'
import { clearUserSettings } from './userSettingsSlice'
import { clearDailyCheckIn } from './dailyCheckInsSlice'
import { clearUI } from './uiSlice'

// Async thunks
export const signUpUser = createAsyncThunk(
  'auth/signUp',
  async ({ fullName, email, password }, { rejectWithValue }) => {
    try {
      const { data, error } = await authAPI.signUp(email, password, fullName)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const signInUser = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data, error } = await authAPI.signIn(email, password)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const signOutUser = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Local sign-out - only clears current device/browser session
      const { error } = await authAPI.signOut()
      if (error) throw error

      // Clear user settings from Redux state on logout
      dispatch(clearUserSettings())
      dispatch(clearDailyCheckIn())
      dispatch(clearUI())
      console.log('[Auth] Local sign-out successful')
      return null
    } catch (error) {
      console.error('[Auth] Local sign-out failed:', error)
      return rejectWithValue(error.message)
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const { data, error } = await authAPI.resetPassword(email)
      if (error) throw error
      return data
    } catch (error) {
      // Improve common error messages for better UX
      let message = error.message
      if (message.includes('you can only request this after')) {
        message = 'Please wait a moment before requesting another reset email.'
      }
      return rejectWithValue(message)
    }
  }
)

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const { session, error } = await authAPI.getSession()
      if (error) throw error
      return session
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const challengeMFA = createAsyncThunk(
  'auth/challengeMFA',
  async ({ factorId }, { rejectWithValue, dispatch }) => {
    try {
      const { mfaAPI } = await import('../utils/supabase')
      const { data, error } = await mfaAPI.createChallenge(factorId)
      if (error) throw error
      
      dispatch(startMFAChallenge({
        factorId,
        challengeId: data.id
      }))
      
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const verifyMFA = createAsyncThunk(
  'auth/verifyMFA',
  async ({ factorId, challengeId, code }, { rejectWithValue, dispatch }) => {
    try {
      const { mfaAPI, authAPI } = await import('../utils/supabase')
      
      // Verify the MFA code
      const { data, error } = await mfaAPI.verifyExistingFactor(factorId, challengeId, code)
      if (error) throw error
      
      // Get updated session with AAL2
      const { session: updatedSession, error: sessionError } = await authAPI.getSession()
      if (sessionError) throw sessionError
      
      // Clear MFA challenge - let ProtectedRoute handle auth state
      dispatch(clearMFAChallenge())
      // Trigger immediate AAL re-check
      dispatch(triggerAALRecheck())
      
      return updatedSession
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const triggerAALRecheck = createAsyncThunk(
  'auth/triggerAALRecheck',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { mfaAPI, authAPI } = await import('../utils/supabase')
      
      // Get current session
      const { session, error: sessionError } = await authAPI.getSession()
      if (sessionError) throw sessionError
      
      if (session?.user) {
        // Check if user has MFA enabled
        const { hasMFA } = await mfaAPI.checkUserMFAStatus()
        
        if (hasMFA) {
          // Check current AAL level
          const { supabase } = await import('../utils/supabase')
          const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
          
          // If user has reached AAL2, set auth state
          if (aalData?.currentLevel === 'aal2') {
            dispatch(setAuthState({ 
              session, 
              user: session.user 
            }))
            return { aalLevel: 'aal2', authenticated: true }
          } else {
            return { aalLevel: aalData?.currentLevel || 'aal1', authenticated: false }
          }
        } else {
          // No MFA required - set auth state
          dispatch(setAuthState({ 
            session, 
            user: session.user 
          }))
          return { aalLevel: 'aal1', authenticated: true }
        }
      }
      
      return { aalLevel: null, authenticated: false }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  user: null,
  session: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  initialized: false,
  resetPasswordSent: false,
  hasMFAEnabled: false,
  mfaChallenge: {
    required: false,
    factorId: null,
    challengeId: null,
    step: 'idle' // 'idle' | 'challenging' | 'verifying'
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearResetPasswordSent: (state) => {
      state.resetPasswordSent = false
    },
    setAuthState: (state, action) => {
      const { session, user } = action.payload
      state.session = session
      state.user = user
      state.isAuthenticated = !!session
      state.initialized = true
    },
    clearAuthState: (state) => {
      state.session = null
      state.user = null
      state.isAuthenticated = false
      state.hasMFAEnabled = false
    },
    setMFAStatus: (state, action) => {
      state.hasMFAEnabled = action.payload
    },
    setMFAChallenge: (state, action) => {
      state.mfaChallenge = action.payload
    },
    clearMFAChallenge: (state) => {
      state.mfaChallenge = {
        required: false,
        factorId: null,
        challengeId: null,
        step: 'idle'
      }
    },
    startMFAChallenge: (state, action) => {
      state.mfaChallenge = {
        required: true,
        factorId: action.payload.factorId,
        challengeId: action.payload.challengeId,
        step: 'challenging'
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Sign Up
      .addCase(signUpUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.loading = false
        // Let ProtectedRoute handle setting authentication state to ensure MFA compliance
        // state.user = action.payload.user
        // state.session = action.payload.session
        // state.isAuthenticated = !!action.payload.session
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Sign In
      .addCase(signInUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.loading = false
        // Let ProtectedRoute handle setting authentication state to ensure MFA compliance
        // state.user = action.payload.user
        // state.session = action.payload.session
        // state.isAuthenticated = !!action.payload.session
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Sign Out
      .addCase(signOutUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.loading = false
        // Always clear all auth state on sign out (fail-safe pattern)
        state.user = null
        state.session = null
        state.isAuthenticated = false
        state.hasMFAEnabled = false
        state.mfaChallenge = {
          required: false,
          factorId: null,
          challengeId: null,
          step: 'idle'
        }
      })
      .addCase(signOutUser.rejected, (state, action) => {
        // This should never happen now, but if it does, still clear auth state (fail-safe)
        state.loading = false
        state.error = action.payload
        state.user = null
        state.session = null
        state.isAuthenticated = false
        state.hasMFAEnabled = false
        state.mfaChallenge = {
          required: false,
          factorId: null,
          challengeId: null,
          step: 'idle'
        }
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true
        state.error = null
        state.resetPasswordSent = false
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false
        state.resetPasswordSent = true
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false
        // Let ProtectedRoute handle setting authentication state to ensure MFA compliance
        // state.session = action.payload
        // state.user = action.payload?.user || null
        // state.isAuthenticated = !!action.payload
        state.initialized = true
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = true
      })

      // Challenge MFA
      .addCase(challengeMFA.pending, (state) => {
        state.mfaChallenge.step = 'challenging'
      })
      .addCase(challengeMFA.fulfilled, (state) => {
        state.mfaChallenge.step = 'verifying'
      })
      .addCase(challengeMFA.rejected, (state, action) => {
        state.error = action.payload
        state.mfaChallenge.step = 'idle'
      })

      // Verify MFA
      .addCase(verifyMFA.pending, (state) => {
        state.loading = true
      })
      .addCase(verifyMFA.fulfilled, (state) => {
        state.loading = false
        state.mfaChallenge.step = 'idle'
      })
      .addCase(verifyMFA.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.mfaChallenge.step = 'verifying'
      })
  }
})

export const { 
  clearError, 
  clearResetPasswordSent, 
  setAuthState, 
  clearAuthState, 
  setMFAStatus,
  setMFAChallenge,
  clearMFAChallenge,
  startMFAChallenge
} = authSlice.actions

// Selectors
export const selectUser = (state) => state.auth.user
export const selectSession = (state) => state.auth.session
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectAuthLoading = (state) => state.auth.loading
export const selectAuthError = (state) => state.auth.error
export const selectAuthInitialized = (state) => state.auth.initialized
export const selectHasMFAEnabled = (state) => state.auth.hasMFAEnabled

export default authSlice.reducer
